import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { Company, SearchRequest } from 'types';
import { filterEmptyFields } from 'utils/object';

import { apiQuery } from '../../query';

import {
  MyCompanyInfo,
  CompanyFormValues,
  CompanyEditFormValues,
  ChangeCompanySettingsRequest,
} from './types';

const tagTypes = ['Company'];

export const companyApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'companyApi',
  endpoints: (build) => ({
    myCompanyInfo: build.query<MyCompanyInfo, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.MY_COMPANY_INFO,
      }),
    }),
    get: build.query<Company, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.COMPANY_DETAILS.replace(':id', id),
      }),
    }),
    remove: build.mutation<void, string>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'delete',
        url: URL.COMPANY_DETAILS.replace(':id', id),
      }),
    }),
    list: build.query<Company[], SearchRequest | void>({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.COMPANY_LIST, { search: params?.search }),
      }),
    }),
    create: build.mutation<void, CompanyFormValues>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        method: 'post',
        url: URL.COMPANY_LIST,
        data: filterEmptyFields({
          ...data,
        }),
      }),
    }),
    changeSettings: build.mutation<Company, ChangeCompanySettingsRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.UPDATE_COMPANY_SETTINGS.replace(':id', id),
      }),
    }),
    edit: build.mutation<Company, CompanyEditFormValues>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        method: 'patch',
        url: URL.COMPANY_DETAILS.replace(':id', id),
        data: {
          id: Number(id),
          ...data,
        },
      }),
    }),
  }),
});

export const {
  useGetQuery,
  useListQuery,
  useEditMutation,
  useRemoveMutation,
  useCreateMutation,
  useMyCompanyInfoQuery,
  useChangeSettingsMutation,
} = companyApi;

export { tagTypes };
export type { MyCompanyInfo };
